import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  CircleAlert,
  Clock,
  Download,
  FileSpreadsheet,
  Inbox,
  Megaphone,
  Send,
  Sparkles,
  Timer,
  Zap,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Badge, PriorityBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select, Textarea } from "@/components/ui/Field";
import { StatTile } from "@/components/ui/StatTile";
import { useToast } from "@/components/ui/toast";
import { Alert, EmptyState, LoadingPanel } from "@/components/ui/Feedback";
import { DataTable, formatDate, type Column } from "@/components/ui/DataTable";

/** Chart palette. Ordered so adjacent slices stay distinguishable. */
const SERIES = [
  "var(--color-flux-400)",
  "var(--color-info-500)",
  "var(--color-monitor-500)",
  "var(--color-routine-500)",
  "var(--color-urgent-500)",
  "var(--color-critical-500)",
];

const tooltipStyle = {
  background: "var(--surface)",
  border: "1px solid var(--hairline-strong)",
  borderRadius: "0.75rem",
  fontSize: "13px",
  boxShadow: "var(--shadow-float)",
  color: "var(--content)",
} as const;

function ScreenError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Alert tone="danger" title="Gagal memuat data" className="mb-5">
      {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
    </Alert>
  );
}

// --------------------------------------------------------------------------
// Analytics
// --------------------------------------------------------------------------

const countRow = z.object({ label: z.string(), value: z.number() });

const analyticsSchema = z.object({
  total: z.number(),
  intents: z.array(countRow).default([]),
  channels: z.array(countRow).default([]),
  priorities: z.array(countRow).default([]),
  monthly: z.array(countRow).default([]),
});

export function AdminAnalytics() {
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "analytics"],
    queryFn: ({ signal }) => api.get("/api/admin/analytics", analyticsSchema, signal),
  });

  return (
    <>
      <PageHeader
        eyebrow="Analitik"
        title="Distribusi layanan"
        description="Komposisi percakapan berdasarkan intent, kanal, dan prioritas."
      />
      <ScreenError error={error} />

      {isPending ? (
        <Card>
          <LoadingPanel label="Memuat analitik" />
        </Card>
      ) : data ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile label="Total percakapan" value={data.total} />
            <StatTile label="Jenis intent" value={data.intents.length} />
            <StatTile label="Kanal aktif" value={data.channels.length} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <Card>
              <CardHeader eyebrow="Topik" title="Intent terbanyak" />
              <CardBody>
                {data.intents.length === 0 ? (
                  <EmptyState icon={<Inbox className="size-5" />} title="Belum ada data" />
                ) : (
                  <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={data.intents}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                      >
                        <CartesianGrid
                          horizontal={false}
                          strokeDasharray="3 5"
                          stroke="var(--hairline)"
                        />
                        <XAxis
                          type="number"
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          tick={{ fill: "var(--content-subtle)", fontSize: 11 }}
                        />
                        <YAxis
                          type="category"
                          dataKey="label"
                          width={110}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "var(--content-muted)", fontSize: 11 }}
                        />
                        <Tooltip cursor={{ fill: "var(--surface-sunken)" }} contentStyle={tooltipStyle} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="var(--color-flux-400)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader eyebrow="Asal" title="Kanal masuk" />
              <CardBody>
                {data.channels.length === 0 ? (
                  <EmptyState icon={<Inbox className="size-5" />} title="Belum ada data" />
                ) : (
                  <div className="h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.channels}
                          dataKey="value"
                          nameKey="label"
                          // Explicit centre: without it the slices are laid out
                          // against a container that has not been measured yet
                          // and the chart renders empty.
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={2}
                          stroke="var(--surface)"
                          strokeWidth={2}
                          isAnimationActive={false}
                        >
                          {data.channels.map((_, index) => (
                            <Cell key={index} fill={SERIES[index % SERIES.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                  {data.channels.map((item, index) => (
                    <li key={item.label} className="flex items-center gap-1.5 text-xs">
                      <span
                        aria-hidden
                        className="size-2 rounded-full"
                        style={{ background: SERIES[index % SERIES.length] }}
                      />
                      {item.label}
                      <span className="text-content-subtle tabular">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader eyebrow="Urgensi" title="Sebaran prioritas" />
            <CardBody className="flex flex-wrap gap-2">
              {data.priorities.map((item) => (
                <span
                  key={item.label}
                  className="border-hairline flex items-center gap-2 rounded-xl border px-3 py-2 text-sm"
                >
                  <PriorityBadge priority={item.label} />
                  <span className="tabular font-bold">{item.value}</span>
                </span>
              ))}
            </CardBody>
          </Card>
        </div>
      ) : null}
    </>
  );
}

// --------------------------------------------------------------------------
// Reports
// --------------------------------------------------------------------------

const reportsSchema = z.object({
  total: z.number(),
  first: z.string().nullable().default(null),
  last: z.string().nullable().default(null),
  exports: z
    .array(
      z.looseObject({
        actor: z.string().nullable().default(null),
        action: z.string(),
        target: z.string().nullable().default(null),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

export function AdminReports() {
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "reports"],
    queryFn: ({ signal }) => api.get("/api/admin/reports", reportsSchema, signal),
  });

  return (
    <>
      <PageHeader
        eyebrow="Ekspor"
        title="Pusat Laporan"
        description="Unduh rekap percakapan dalam format PDF atau Excel."
      />
      <ScreenError error={error} />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label="Total percakapan" value={data?.total ?? "—"} />
        <StatTile
          label="Data sejak"
          value={data?.first ? formatDate(data.first) : "—"}
          className="[&_p:nth-child(2)]:text-xl"
        />
        <StatTile
          label="Data terakhir"
          value={data?.last ? formatDate(data.last) : "—"}
          className="[&_p:nth-child(2)]:text-xl"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader eyebrow="Unduh" title="Format laporan" />
          <CardBody className="space-y-2.5">
            {/* Plain anchors, not fetch: these endpoints stream a file and the
                browser's own download handling is what should receive it. */}
            {[
              { href: "/export/pdf", label: "Laporan PDF", detail: "Ringkasan percakapan siap cetak", Icon: Download },
              { href: "/export/excel", label: "Laporan Excel", detail: "Multi-sheet dengan formatting", Icon: FileSpreadsheet },
            ].map(({ href, label, detail, Icon }) => (
              <a
                key={href}
                href={href}
                className="border-hairline hover:border-accent/40 hover:bg-surface-sunken flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
              >
                <Icon aria-hidden className="text-content-subtle size-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className="text-content-subtle block text-xs">{detail}</span>
                </span>
              </a>
            ))}
            <a
              href="/admin/backup"
              className="border-hairline hover:border-accent/40 hover:bg-surface-sunken flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
            >
              <Download aria-hidden className="text-content-subtle size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Backup database</span>
                <span className="text-content-subtle block text-xs">
                  Salinan penuh — simpan di tempat aman
                </span>
              </span>
            </a>
          </CardBody>
        </Card>

        <Card>
          <CardHeader eyebrow="Jejak" title="Ekspor terakhir" />
          {isPending ? (
            <LoadingPanel />
          ) : data && data.exports.length > 0 ? (
            <ul className="divide-hairline divide-y">
              {data.exports.map((item, index) => (
                <li key={index} className="flex items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.actor ?? "—"}</p>
                    <p className="text-content-subtle truncate text-xs">{item.target ?? "—"}</p>
                  </div>
                  <Badge>{item.action.replace("EXPORT_", "")}</Badge>
                  <span className="text-content-subtle shrink-0 text-xs">
                    {formatDate(item.created_at, true)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Inbox className="size-5" />}
              title="Belum ada ekspor"
              description="Riwayat unduhan akan tercatat di sini."
            />
          )}
        </Card>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Broadcast
// --------------------------------------------------------------------------

const broadcastSchema = z.object({
  templates: z
    .array(
      z.looseObject({
        id: z.number(),
        name: z.string(),
        message_template: z.string(),
        created_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
  logs: z
    .array(
      z.looseObject({
        id: z.number(),
        template_name: z.string().nullable().default(null),
        recipient_count: z.number().nullable().default(null),
        status: z.string().nullable().default(null),
        sent_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
});

const TARGETS = [
  { value: "all_patients", label: "Semua pasien terdaftar" },
  { value: "active_bookings", label: "Pasien dengan janji temu aktif" },
  { value: "custom", label: "Nomor tertentu" },
];

const sendResultSchema = z.looseObject({
  success: z.boolean().default(false),
  log_id: z.number().nullable().default(null),
  recipient_count: z.number().nullable().default(null),
});

export function AdminBroadcast() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "broadcast"],
    queryFn: ({ signal }) => api.get("/api/admin/broadcast", broadcastSchema, signal),
  });

  const [templateId, setTemplateId] = useState("");
  const [target, setTarget] = useState(TARGETS[0].value);
  const [customNumbers, setCustomNumbers] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const selectedTemplate = data?.templates.find((item) => String(item.id) === templateId);

  const send = useMutation({
    mutationFn: () =>
      api.post("/api/broadcast/send", sendResultSchema, {
        template_id: Number(templateId),
        target,
        custom_numbers: customNumbers,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "broadcast"] });
      setConfirming(false);
      setAcknowledged(false);
      toast.success({
        title: "Broadcast dikirim",
        description:
          result.recipient_count != null
            ? `${result.recipient_count} penerima diproses.`
            : undefined,
      });
    },
    onError: (cause) =>
      toast.danger({
        title: "Broadcast gagal",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  return (
    <>
      <PageHeader
        eyebrow="Komunikasi massal"
        title="Broadcast"
        description="Kirim pesan edukasi ke pasien dan tinjau riwayat pengirimannya."
      />
      <ScreenError error={error} />

      <Card className="mb-4">
        <CardHeader
          eyebrow="Kirim"
          title="Susun pengiriman"
          description="Pesan massal tidak dapat ditarik kembali setelah terkirim."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Template pesan"
              value={templateId}
              onChange={(event) => {
                setTemplateId(event.target.value);
                setConfirming(false);
              }}
            >
              <option value="">Pilih template</option>
              {data?.templates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
            <Select
              label="Penerima"
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
                setConfirming(false);
              }}
            >
              {TARGETS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </div>

          {target === "custom" && (
            <Textarea
              label="Nomor tujuan"
              placeholder="0812xxxxxxx, 0813xxxxxxx"
              value={customNumbers}
              onChange={(event) => setCustomNumbers(event.target.value)}
              hint="Pisahkan dengan koma atau baris baru."
            />
          )}

          {selectedTemplate && (
            <div className="bg-surface-sunken rounded-xl px-4 py-3">
              <p className="eyebrow mb-1.5">Pratinjau</p>
              <p className="text-sm whitespace-pre-line">
                {selectedTemplate.message_template}
              </p>
            </div>
          )}

          {!confirming ? (
            <Button
              variant="primary"
              disabled={!templateId || (target === "custom" && !customNumbers.trim())}
              onClick={() => setConfirming(true)}
              icon={<Megaphone className="size-4" />}
            >
              Lanjut ke konfirmasi
            </Button>
          ) : (
            // Two-step by design: a mass message to real patients is not
            // reversible, so sending must be a deliberate second decision
            // rather than one stray click on a list screen.
            <div className="border-critical-500/40 bg-critical-tint space-y-3 rounded-xl border p-4">
              <p className="text-sm font-bold">Konfirmasi pengiriman</p>
              <p className="text-sm">
                Template <b>{selectedTemplate?.name}</b> akan dikirim ke{" "}
                <b>{TARGETS.find((item) => item.value === target)?.label.toLowerCase()}</b>. Pesan
                yang sudah terkirim tidak dapat ditarik kembali.
              </p>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  className="accent-critical-500 mt-0.5 size-4 shrink-0"
                />
                <span className="text-sm">
                  Saya sudah mendapat persetujuan supervisor dan memastikan isi pesan benar.
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="danger"
                  disabled={!acknowledged}
                  loading={send.isPending}
                  onClick={() => send.mutate()}
                  icon={<Send className="size-4" />}
                >
                  Kirim sekarang
                </Button>
                <Button
                  onClick={() => {
                    setConfirming(false);
                    setAcknowledged(false);
                  }}
                >
                  Batal
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <Card>
          <CardHeader
            eyebrow="Template"
            title="Pesan tersimpan"
            action={<Badge>{data?.templates.length ?? 0}</Badge>}
          />
          {isPending ? (
            <LoadingPanel />
          ) : data && data.templates.length > 0 ? (
            <ul className="divide-hairline divide-y">
              {data.templates.map((template) => (
                <li key={template.id} className="px-5 py-3.5">
                  <p className="text-sm font-semibold">{template.name}</p>
                  <p className="text-content-muted mt-1 line-clamp-3 text-xs whitespace-pre-line">
                    {template.message_template}
                  </p>
                  <p className="text-content-subtle mt-1.5 text-2xs">
                    {formatDate(template.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Megaphone className="size-5" />} title="Belum ada template" />
          )}
        </Card>

        <Card>
          <CardHeader eyebrow="Riwayat" title="Pengiriman terakhir" />
          {isPending ? (
            <LoadingPanel />
          ) : data && data.logs.length > 0 ? (
            <ul className="divide-hairline divide-y">
              {data.logs.map((log) => (
                <li key={log.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{log.template_name ?? "—"}</p>
                    <p className="text-content-subtle text-xs">
                      {log.recipient_count ?? 0} penerima · {formatDate(log.sent_at, true)}
                    </p>
                  </div>
                  <Badge tone={log.status === "completed" ? "routine" : "monitor"}>
                    {log.status ?? "—"}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Inbox className="size-5" />} title="Belum ada pengiriman" />
          )}
        </Card>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Intelligence
// --------------------------------------------------------------------------

const intelligenceSchema = z.object({
  usage: z.object({
    calls: z.number(),
    credits: z.number(),
    avg_latency: z.number(),
    failures: z.number(),
  }),
  models: z
    .array(
      z.object({
        model: z.string(),
        calls: z.number(),
        latency: z.number(),
        credits: z.number(),
        failures: z.number(),
      }),
    )
    .default([]),
  sla: z
    .array(
      z.looseObject({
        ticket: z.string(),
        patient: z.string().nullable().default(null),
        status: z.string().nullable().default(null),
        priority: z.string().nullable().default(null),
        assigned_to: z.string().nullable().default(null),
        waiting_minutes: z.number(),
      }),
    )
    .default([]),
  reminders: z.array(z.looseObject({ status: z.string(), total: z.number() })).default([]),
  governance: z.array(z.looseObject({ status: z.string(), total: z.number() })).default([]),
});

type SlaRow = z.infer<typeof intelligenceSchema>["sla"][number];

function waitingTone(minutes: number) {
  if (minutes >= 120) return "critical" as const;
  if (minutes >= 60) return "urgent" as const;
  if (minutes >= 30) return "monitor" as const;
  return "routine" as const;
}

export function AdminIntelligence() {
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "intelligence"],
    queryFn: ({ signal }) => api.get("/api/admin/intelligence", intelligenceSchema, signal),
    refetchInterval: 60_000,
  });

  const columns: Column<SlaRow>[] = [
    {
      key: "ticket",
      header: "Tiket",
      width: "160px",
      render: (row) => <span className="tabular font-semibold">{row.ticket}</span>,
    },
    { key: "patient", header: "Pasien", render: (row) => row.patient ?? "Tamu" },
    {
      key: "status",
      header: "Status",
      width: "150px",
      render: (row) => <Badge>{row.status ?? "—"}</Badge>,
    },
    {
      key: "assigned",
      header: "Ditangani",
      width: "140px",
      render: (row) => row.assigned_to ?? "Belum diklaim",
    },
    {
      key: "waiting",
      header: "Menunggu",
      align: "right",
      width: "130px",
      render: (row) => (
        <Badge tone={waitingTone(row.waiting_minutes)}>
          {row.waiting_minutes < 60
            ? `${row.waiting_minutes} mnt`
            : `${Math.floor(row.waiting_minutes / 60)} jam`}
        </Badge>
      ),
    },
  ];

  const failureRate =
    data && data.usage.calls > 0
      ? Math.round((data.usage.failures / data.usage.calls) * 100)
      : 0;

  return (
    <>
      <PageHeader
        eyebrow="Operasional AI"
        title="Intelligence Center"
        description="Pemakaian model, keandalannya, dan antrean yang menunggu paling lama."
      />
      <ScreenError error={error} />

      {isPending ? (
        <Card>
          <LoadingPanel label="Memuat metrik AI" />
        </Card>
      ) : data ? (
        <div className="space-y-4">
          {failureRate >= 10 && (
            <Alert tone="warning" title={`${failureRate}% panggilan AI gagal dalam 30 hari`}>
              Periksa kuota provider dan konfigurasi model sebelum kegagalan mengganggu pasien.
            </Alert>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Panggilan AI"
              value={data.usage.calls}
              caption="30 hari terakhir"
              icon={<Sparkles className="size-4" />}
            />
            <StatTile
              label="Kredit terpakai"
              value={data.usage.credits}
              icon={<Zap className="size-4" />}
            />
            <StatTile
              label="Latensi rata-rata"
              value={data.usage.avg_latency}
              unit="ms"
              icon={<Timer className="size-4" />}
            />
            <StatTile
              label="Kegagalan"
              value={data.usage.failures}
              caption={`${failureRate}% dari total`}
              icon={<CircleAlert className="size-4" />}
            />
          </div>

          {/* The SLA queue is the operational table on this page and needs the
              full width; cramming it beside the model list forced a horizontal
              scroll on the one thing an operator reads top-to-bottom. */}
          <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
            <Card>
              <CardHeader eyebrow="Per model" title="Pemakaian" />
              {data.models.length === 0 ? (
                <EmptyState icon={<Activity className="size-5" />} title="Belum ada panggilan AI" />
              ) : (
                <ul className="divide-hairline divide-y">
                  {data.models.map((model) => (
                    <li key={model.model} className="px-5 py-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="truncate font-mono text-xs font-semibold">{model.model}</p>
                        <span className="tabular shrink-0 text-sm font-bold">{model.calls}</span>
                      </div>
                      <p className="text-content-subtle mt-0.5 text-xs">
                        {model.latency} ms · {model.credits} kredit
                        {model.failures > 0 && (
                          <span className="text-critical-text"> · {model.failures} gagal</span>
                        )}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader eyebrow="Ringkasan" title="Pengingat" />
              <CardBody className="flex flex-wrap gap-2">
                {data.reminders.length === 0 ? (
                  <p className="text-content-subtle text-sm">Belum ada data.</p>
                ) : (
                  data.reminders.map((row) => (
                    <span
                      key={row.status}
                      className="border-hairline flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm"
                    >
                      <span className="capitalize">{row.status}</span>
                      <span className="tabular font-bold">{row.total}</span>
                    </span>
                  ))
                )}
              </CardBody>
              <CardHeader eyebrow="Ringkasan" title="Governance knowledge" />
              <CardBody className="flex flex-wrap gap-2">
                {data.governance.length === 0 ? (
                  <p className="text-content-subtle text-sm">Belum ada data.</p>
                ) : (
                  data.governance.map((row) => (
                    <span
                      key={row.status}
                      className="border-hairline flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm"
                    >
                      <span className="capitalize">{row.status}</span>
                      <span className="tabular font-bold">{row.total}</span>
                    </span>
                  ))
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader
              eyebrow="SLA"
              title="Antrean menunggu terlama"
              description="Diurutkan sesuai urutan pengerjaan sebenarnya."
            />
            <DataTable
              columns={columns}
              rows={data.sla}
              rowKey={(row) => row.ticket}
              empty={{
                icon: <Clock className="size-5" />,
                title: "Tidak ada antrean",
                description: "Semua percakapan sudah tertangani.",
              }}
            />
          </Card>

        </div>
      ) : null}
    </>
  );
}
